import { useEffect, useMemo, useState } from "react";
import Close from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { capitalize } from "lodash-es";
import { useTranslation } from "react-i18next";

import { SubmissionButton } from "@thalamos/frontend";
import type {
  AvailableOrganisationUnbranded,
  ExtendedVisionUser,
  UserOrganisationRole,
} from "@thalamos/vision-common";
import {
  OrganisationRoleSchema,
  tk,
  UserOrganisationRoleSchema,
} from "@thalamos/vision-common";

import { apiHooks } from "../../../api/index.js";
import { PopupDialog } from "../../Dialog/PopupDialog.js";
import { TypographyI18N } from "../../TypographyI18N/TypographyI18N.js";

export interface EditUserMembershipModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    selectedRoles: { [key: string]: boolean },
    roleOrganisations: { [key: string]: AvailableOrganisationUnbranded },
    supervisorSyncExemption: boolean,
  ) => Promise<void>;
  user: ExtendedVisionUser;
}

export function EditUserMembershipModal({
  open,
  onClose,
  onConfirm,
  user,
}: EditUserMembershipModalProps) {
  const organisationId = user.memberships[0]?.organisationId;
  if (!organisationId) {
    return (
      <PopupDialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        disableDialogRootPadding={true}
      >
        <DialogTitle>{user.name}</DialogTitle>
        <DialogContent>
          <TypographyI18N
            translationKey={tk("admin.users.modal.noMembership")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            <TypographyI18N translationKey={tk("common.close")} />
          </Button>
        </DialogActions>
      </PopupDialog>
    );
  }

  const [{ data: organisationsResponse }] =
    apiHooks.organisation.getAvailableOrganisations(organisationId);

  const availableOrganisations: AvailableOrganisationUnbranded[] =
    Array.isArray(organisationsResponse) ? organisationsResponse : [];

  return (
    <EditUserMembershipModalInner
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      user={user}
      availableOrganisations={availableOrganisations}
    />
  );
}

type EditUserMembershipModalInnerProps = EditUserMembershipModalProps & {
  availableOrganisations: AvailableOrganisationUnbranded[];
};

export function EditUserMembershipModalInner({
  open,
  onClose,
  onConfirm,
  user,
  availableOrganisations,
}: EditUserMembershipModalInnerProps) {
  const commandUnitsOnly = useMemo(() => {
    return availableOrganisations.filter(
      (org) =>
        org.role === OrganisationRoleSchema.enum.PoliceCommandUnit ||
        org.role === OrganisationRoleSchema.enum.PoliceForce,
    );
  }, [availableOrganisations]);

  // filter out PoliceGeographicUnit and orgs excluded as incident/detention locations
  const withoutPoliceGeographicUnit = availableOrganisations.filter(
    (org) =>
      org.role !== OrganisationRoleSchema.enum.PoliceGeographicUnit &&
      !org.excludeAsIncidentLocation,
  );

  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const availableRoles = [
    UserOrganisationRoleSchema.enum.Officer,
    UserOrganisationRoleSchema.enum.Supervisor,
  ] as const satisfies UserOrganisationRole[];

  type AvailableRole = (typeof availableRoles)[number];

  const [selectedRoles, setSelectedRoles] = useState<
    Record<AvailableRole, boolean>
  >({
    [UserOrganisationRoleSchema.enum.Officer]: false,
    [UserOrganisationRoleSchema.enum.Supervisor]: false,
  });

  const [roleOrganisations, setRoleOrganisations] = useState<
    Partial<Record<AvailableRole, AvailableOrganisationUnbranded>>
  >({});

  const [supervisorSyncExemption, setSupervisorSyncExemption] = useState(
    user.supervisorSyncExemption,
  );

  useEffect(() => {
    if (open && Array.isArray(user.memberships)) {
      const rolesFromUser = user.memberships.map((m) => m.role);
      const initialRoles = Object.fromEntries(
        availableRoles.map((role) => [role, rolesFromUser.includes(role)]),
      ) as Record<AvailableRole, boolean>;

      const initialOrgSelections = Object.fromEntries(
        availableRoles
          .map((role) => {
            const membership = user.memberships.find((m) => m.role === role);
            const fullOrg = availableOrganisations.find(
              (org) => org.id === membership?.organisationId,
            );
            return fullOrg ? [role, fullOrg] : null;
          })
          .filter(Boolean) as [string, AvailableOrganisationUnbranded][],
      );

      setSelectedRoles(initialRoles);
      setRoleOrganisations(initialOrgSelections);
      setSupervisorSyncExemption(user.supervisorSyncExemption);
    }
  }, [open, user, availableOrganisations]);

  const isValid =
    Object.values(selectedRoles).some(Boolean) &&
    availableRoles.every((role) => {
      const selected = selectedRoles[role];
      return !selected || !!roleOrganisations[role];
    });

  return (
    <PopupDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      disableDialogRootPadding={true}
    >
      <DialogTitle sx={{ fontSize: "1.250rem", fontWeight: "bold" }}>
        {t("admin.users.modal.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          edge="end"
          sx={{ position: "absolute", right: 15, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography
          sx={{ color: "primary", fontSize: "0.875rem" }}
          gutterBottom
        >
          {t("admin.users.modal.message")}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.875rem",
            borderLeft: `2px solid ${theme.palette.primary.main}`,
            paddingLeft: theme.spacing(2),
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(0.5),
          }}
        >
          <span>{user.name}</span>
          <span>{user.email}</span>
          <span>{user.shoulderNumber}</span>
        </Typography>

        <Typography
          sx={{
            fontSize: "0.875rem",
            color: theme.palette.text.secondary,
            mt: 2,
            mb: 1,
          }}
        >
          {t("admin.users.modal.role")}
        </Typography>

        {availableRoles.map((role) => {
          const orgsForRole =
            role === UserOrganisationRoleSchema.enum.Officer
              ? commandUnitsOnly
              : withoutPoliceGeographicUnit;

          const labelText = `${capitalize(role)} ${t("admin.users.modal.organisation")}`;

          return (
            <div key={role} style={{ marginBottom: "1rem" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid={`edit-user-checkbox-${role}`}
                    checked={selectedRoles[role]}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedRoles((prev) => ({
                        ...prev,
                        [role]: checked,
                      }));
                    }}
                  />
                }
                label={t(`admin.users.modal.${role}`) ?? role}
              />

              {orgsForRole.length > 0 && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel id={`org-label-${role}`}>{labelText}</InputLabel>
                  <Select
                    labelId={`org-label-${role}`}
                    label={labelText}
                    data-testid={`edit-user-select-${role}`}
                    disabled={!selectedRoles[role]}
                    value={roleOrganisations[role]?.id ?? ""}
                    onChange={(e) => {
                      const selectedOrg = orgsForRole.find(
                        (org) => org.id === e.target.value,
                      );
                      if (selectedOrg) {
                        setRoleOrganisations((prev) => ({
                          ...prev,
                          [role]: selectedOrg,
                        }));
                      }
                    }}
                  >
                    {orgsForRole.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </div>
          );
        })}

        {selectedRoles[UserOrganisationRoleSchema.enum.Supervisor] && (
          <>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  data-testid="edit-user-checkbox-supervisor-sync-exemption"
                  checked={supervisorSyncExemption}
                  onChange={(e) => {
                    setSupervisorSyncExemption(e.target.checked);
                  }}
                />
              }
              label={t("admin.users.modal.supervisorSyncExemption")}
            />

            {supervisorSyncExemption && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t("admin.users.modal.supervisorSyncExemptionBanner")}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 3,
        }}
      >
        <Button onClick={onClose} color="error" variant="outlined">
          {t("common.close")}
        </Button>
        <SubmissionButton
          data-testid="edit-user-modal-confirm-button"
          onClick={async () => {
            const filteredRoleOrgs: Partial<
              Record<AvailableRole, AvailableOrganisationUnbranded>
            > = {};

            availableRoles.forEach((role) => {
              if (selectedRoles[role] && roleOrganisations[role]) {
                filteredRoleOrgs[role] = roleOrganisations[role];
              }
            });

            await onConfirm(
              selectedRoles,
              filteredRoleOrgs,
              supervisorSyncExemption,
            );
          }}
          color="primary"
          variant="contained"
          disabled={!isValid}
        >
          {t("admin.users.modal.update")}
        </SubmissionButton>
      </DialogActions>
    </PopupDialog>
  );
}
